<div class="card border-0 shadow-sm personal-profile-card">
  <!-- Header -->
  <div class="card-header bg-white">
    <h5 class="mb-1 fw-semibold">
      <i class="bi bi-person-circle text-primary me-2"></i>
      Profile Settings
    </h5>

    <small class="text-muted">
      Keep your personal contact details current so the practice can reach you when it matters.
    </small>
  </div>

  <div class="card-body">
    @if (loading) {
      <div class="text-center py-4 text-muted">
        <div class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
        Loading profile settings...
      </div>
    } @else {
      @if (errorMessage) {
        <div class="alert alert-danger d-flex align-items-center">
          <i class="bi bi-exclamation-triangle-fill me-2"></i>
          <span>{{ errorMessage }}</span>
        </div>
      }

      @if (successMessage) {
        <div class="alert alert-success d-flex align-items-center">
          <i class="bi bi-check-circle-fill me-2"></i>
          <span>{{ successMessage }}</span>
        </div>
      }

      <div class="alert alert-info d-flex align-items-start profile-note">
        <i class="bi bi-info-circle-fill me-2 mt-1"></i>
        <div>
          Your email address and role are managed by the practice. You can update the rest of your
          personal contact details here.
        </div>
      </div>

      <div class="row g-3 mb-4">
        <div class="col-md-4">
          <div class="card profile-metric h-100 border-0">
            <div class="card-body">
              <div class="text-muted small mb-1">Profile Name</div>
              <div class="fs-5 fw-bold text-truncate">
                {{ profile.firstName }} {{ profile.lastName }}
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <div class="card profile-metric h-100 border-0">
            <div class="card-body">
              <div class="text-muted small mb-1">Assigned Role</div>
              <div class="fs-5 fw-bold text-truncate">{{ profile.role || 'Unassigned' }}</div>
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <div class="card profile-metric h-100 border-0">
            <div class="card-body">
              <div class="text-muted small mb-1">Contact Number</div>
              <div class="fs-5 fw-bold text-truncate">{{ profile.phoneNumber || 'Not set' }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Basic Info -->
      <div class="card border-0 bg-light mb-4 profile-form-card">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
            <div>
              <h6 class="mb-1 fw-semibold">Personal Details</h6>
              <small class="text-muted"
                >Update the information the practice uses to contact you.</small
              >
            </div>
            <span class="badge text-bg-primary align-self-center">Profile</span>
          </div>

          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label fw-semibold">First Name</label>
              <input
                type="text"
                class="form-control"
                [(ngModel)]="profile.firstName"
                placeholder="First name"
              />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Last Name</label>
              <input
                type="text"
                class="form-control"
                [(ngModel)]="profile.lastName"
                placeholder="Last name"
              />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Email</label>
              <input
                type="email"
                class="form-control"
                disabled
                [(ngModel)]="profile.email"
                placeholder="email@example.com"
              />
            </div>

            <div class="col-md-6">
              <label class="form-label fw-semibold">Phone Number</label>
              <input
                type="text"
                class="form-control"
                [(ngModel)]="profile.phoneNumber"
                placeholder="+27 ..."
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Job / Role Info -->
      <div class="card border-0 bg-light mb-4 profile-form-card">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label fw-semibold">Role</label>
              <input type="text" class="form-control" [(ngModel)]="profile.role" disabled />
              <small class="text-muted">Assigned by system administrator</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Professional & Licensing Details (Doctor Only - Read Only) -->
      @if (doctorDetails) {
        <div class="card border-0 bg-light mb-4 profile-form-card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
              <div>
                <h6 class="mb-1 fw-semibold">
                  <i class="bi bi-award-fill text-primary me-2"></i>Medical Qualifications & Licensing
                </h6>
                <small class="text-muted"
                  >Professional credentials and license information registered with medical board (read-only).</small
                >
              </div>
              <span class="badge text-bg-info text-white align-self-center">Read-Only</span>
            </div>

            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label fw-semibold">License Number</label>
                <input
                  type="text"
                  class="form-control"
                  [value]="doctorDetails.licenseNumber || 'Not provided'"
                  disabled
                  readonly
                />
              </div>

              <div class="col-md-6">
                <label class="form-label fw-semibold">Practice Number</label>
                <input
                  type="text"
                  class="form-control"
                  [value]="doctorDetails.practiceNumber || 'Not provided'"
                  disabled
                  readonly
                />
              </div>

              <div class="col-md-6">
                <label class="form-label fw-semibold">Highest Qualification</label>
                <input
                  type="text"
                  class="form-control"
                  [value]="doctorDetails.qualification || 'Not provided'"
                  disabled
                  readonly
                />
              </div>

              <div class="col-md-6">
                <label class="form-label fw-semibold">Learning Institution</label>
                <input
                  type="text"
                  class="form-control"
                  [value]="doctorDetails.learningInstitution || 'Not provided'"
                  disabled
                  readonly
                />
              </div>

              <div class="col-md-6">
                <label class="form-label fw-semibold">Qualification Certificate Number</label>
                <input
                  type="text"
                  class="form-control"
                  [value]="doctorDetails.qualificationCertificateNumber || 'Not provided'"
                  disabled
                  readonly
                />
              </div>

              <div class="col-md-6">
                <label class="form-label fw-semibold">Specialization</label>
                <input
                  type="text"
                  class="form-control"
                  [value]="doctorDetails.specialization || 'General Practitioner'"
                  disabled
                  readonly
                />
              </div>

              <div class="col-md-6">
                <label class="form-label fw-semibold">Experience</label>
                <input
                  type="text"
                  class="form-control"
                  [value]="doctorDetails.experienceYears ? (doctorDetails.experienceYears + ' Years') : 'Not specified'"
                  disabled
                  readonly
                />
              </div>
            </div>
          </div>
        </div>
      }

      <!-- Address -->
      <div class="card border-0 bg-light profile-form-card">
        <div class="card-body">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label fw-semibold">Address</label>
              <input
                type="text"
                class="form-control"
                [(ngModel)]="profile.addressLine1"
                placeholder="Practice or home address"
              />
            </div>
          </div>
        </div>
      </div>

      @if (canCreateClinic) {
        <div class="card border-0 bg-light mt-4 profile-form-card">
          <div class="card-body">
            <div class="mb-3">
              <h6 class="mb-1 fw-semibold">Create a Practice</h6>
              <small class="text-muted"
                >Create a practice under your existing doctor account. It will be submitted for
                review.</small
              >
            </div>

            @if (clinicCreationError) {
              <div class="alert alert-danger py-2">{{ clinicCreationError }}</div>
            }

            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label fw-semibold">Practice Name</label>
                <input
                  type="text"
                  class="form-control"
                  [(ngModel)]="clinic.name"
                  [disabled]="creatingClinic"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Practice Number</label>
                <input
                  type="text"
                  class="form-control"
                  [(ngModel)]="clinic.practiceNumber"
                  [disabled]="creatingClinic"
                />
              </div>
              <div class="col-12">
                <label class="form-label fw-semibold">Address Line 1</label>
                <input
                  type="text"
                  class="form-control"
                  [(ngModel)]="clinic.addressLine1"
                  [disabled]="creatingClinic"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Address Line 2</label>
                <input
                  type="text"
                  class="form-control"
                  [(ngModel)]="clinic.addressLine2"
                  [disabled]="creatingClinic"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Address Line 3</label>
                <input
                  type="text"
                  class="form-control"
                  [(ngModel)]="clinic.addressLine3"
                  [disabled]="creatingClinic"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">City</label>
                <input
                  type="text"
                  class="form-control"
                  [(ngModel)]="clinic.city"
                  [disabled]="creatingClinic"
                />
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Postal Code</label>
                <input
                  type="text"
                  class="form-control"
                  [(ngModel)]="clinic.postalCode"
                  [disabled]="creatingClinic"
                />
              </div>
            </div>

            <div class="mt-3 d-flex justify-content-end">
              <button class="btn btn-primary" [disabled]="creatingClinic" (click)="createClinic()">
                <i class="bi bi-hospital me-2"></i
                >{{ creatingClinic ? 'Creating...' : 'Create Practice' }}
              </button>
            </div>
          </div>
        </div>
      }
    }
  </div>

  <!-- Footer -->
  @if (!loading) {
    <div
      class="card-footer bg-white d-flex justify-content-between align-items-center flex-wrap gap-2"
    >
      <small class="text-muted">Saved changes update your practice profile immediately.</small>

      <button class="btn btn-primary" [disabled]="saving" (click)="saveProfile()">
        <i class="bi bi-check-circle me-2"></i>
        {{ saving ? 'Saving...' : 'Save Profile' }}
      </button>
    </div>
  }
</div>
